import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import Header from "./components/dashboard/Header";
import KPICards from "./components/dashboard/KPICards";
import GaugeCluster from "./components/dashboard/GaugeCluster";
import TurnoverTargetEngine from "./components/dashboard/TurnoverTargetEngine";
import DailyCaptureSheet from "./components/dashboard/DailyCaptureSheet";
import VehicleSettingsDialog from "./components/dashboard/VehicleSettingsDialog";
import PasswordGate from "./components/PasswordGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem("cargodash_authenticated") === "true";
  });
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [availableMonths, setAvailableMonths] = useState([]);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [engineSettings, setEngineSettings] = useState({
    office_cost: 216000,
    margin: 33.3,
  });

  const fetchDashboardStats = useCallback(async () => {
    try {
      const [year, month] = selectedMonth.split("-").map(Number);
      const response = await axios.get(`${API}/dashboard-stats`, {
        params: { year, month },
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  const fetchAvailableMonths = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/available-months`);
      setAvailableMonths(response.data.months);
    } catch (error) {
      console.error("Error fetching available months:", error);
    }
  }, []);

  const fetchVehicles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/vehicles`);
      setVehicles(response.data.vehicles || []);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    }
  }, []);

  const fetchEngineSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/engine-settings`);
      setEngineSettings(response.data);
    } catch (error) {
      console.error("Error fetching engine settings:", error);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
    fetchAvailableMonths();
    fetchVehicles();
    fetchEngineSettings();
  }, [fetchDashboardStats, fetchAvailableMonths, fetchVehicles, fetchEngineSettings]);

  const handleDataSaved = () => {
    fetchDashboardStats();
    fetchAvailableMonths();
    toast.success("Data saved successfully");
  };

  const handleEngineSettingsUpdate = async (newSettings) => {
    try {
      await axios.post(`${API}/engine-settings`, newSettings);
      setEngineSettings(newSettings);
      toast.success("Settings saved");
    } catch (error) {
      console.error("Error saving engine settings:", error);
      toast.error("Failed to save settings");
    }
  };

  const handleSettingsSaved = () => {
    fetchVehicles();
    fetchDashboardStats();
    toast.success("Settings saved successfully");
  };

  const formatMonthLabel = (monthStr) => {
    const [year, month] = monthStr.split("-");
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
  };

  // Show password gate if not authenticated
  if (!isAuthenticated) {
    return <PasswordGate onUnlock={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="dashboard-container" data-testid="dashboard-container">
      <Toaster position="top-right" richColors />
      
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <Header
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          availableMonths={availableMonths}
          formatMonthLabel={formatMonthLabel}
          onOpenCapture={() => setCaptureOpen(true)}
          onOpenSettings={() => setSettingsOpen(true)}
        />

        {loading ? (
          <div className="flex items-center justify-center h-96" data-testid="loading-state">
            <div className="text-[#94A3B8]">Loading dashboard...</div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-6"
          >
            {/* Section 1: KPI Summary */}
            <KPICards stats={dashboardStats} settings={engineSettings} />

            {/* Section 2: Turnover Target Engine */}
            <TurnoverTargetEngine 
              data={dashboardStats?.turnover_engine} 
              settings={engineSettings}
              onUpdateSettings={handleEngineSettingsUpdate}
            />

            {/* Section 4: Vehicle Performance Gauges */}
            <GaugeCluster
              vehicles={dashboardStats?.vehicles || []}
            />
          </motion.div>
        )}
      </div>

      {/* Daily Capture Sheet */}
      <DailyCaptureSheet
        open={captureOpen}
        onOpenChange={setCaptureOpen}
        onSaved={handleDataSaved}
        api={API}
        vehicles={vehicles}
      />

      {/* Vehicle Settings Dialog */}
      <VehicleSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSaved={handleSettingsSaved}
        vehicles={vehicles}
        api={API}
      />
    </div>
  );
}

export default App;
